// Rysowanie ukladu przekladni: zebatki i kola osadzone na walach.
// Uzywane przez strone demo (pozniej takze przy podgladzie konstrukcji).

import { getDefinition } from '../domain/catalog';
import type { PartDefinition, Vec2Like } from '../domain/types';
import { METERS_PER_GRID } from '../physics/units';
import type { Camera } from './camera';
import { drawPart, drawWheel } from './partArt';

export interface GearMember {
  typeId: string;
  /** Os walu w kratkach. */
  shaft: Vec2Like;
  /** Kat obrotu walu (radiany). */
  angle: number;
}

/**
 * Lokalny srodek obrotu czesci osadzonej na wale: jej socket 'axle'.
 * To ten sam punkt, ktory `blueprintFor` przystawia do osi walu, wiec grafika
 * obraca sie wokol osi, a nie wokol naroza klatki (origina).
 */
export function shaftPivot(definition: PartDefinition): Vec2Like {
  const axle = definition.sockets.find((socket) => socket.kind === 'axle');
  if (axle) return { x: axle.x, y: axle.y };
  const shape = definition.shapes[0];
  return shape ? { x: shape.x, y: shape.y } : { x: 0.5, y: 0.5 };
}

/** Rysuje jeden element walu: zebatke, sprocket albo kolo. */
export function drawGearMember(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  member: GearMember,
): void {
  const definition = getDefinition(member.typeId);
  const centerX = member.shaft.x * METERS_PER_GRID;
  const centerY = member.shaft.y * METERS_PER_GRID;

  if (definition.wheel) {
    drawWheel(ctx, camera, {
      definition,
      centerX,
      centerY,
      angle: member.angle,
      radiusMeters: (definition.wheel.radiusGrid ?? 0.5) * METERS_PER_GRID,
    });
    return;
  }

  const pivot = shaftPivot(definition);
  drawPart(ctx, camera, {
    definition,
    originX: (member.shaft.x - pivot.x) * METERS_PER_GRID,
    originY: (member.shaft.y - pivot.y) * METERS_PER_GRID,
    pivot,
    angle: member.angle,
  });
}

/**
 * Kreska od osi do obwodu. To ona pozwala ZOBACZYC przelozenie: na jednym
 * obrazku widac, ktory wal kreci sie szybciej i w ktora strone.
 */
export function drawRotationMarker(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  member: GearMember,
  color = '#ffd479',
): void {
  const definition = getDefinition(member.typeId);
  const center = camera.worldToScreen(
    member.shaft.x * METERS_PER_GRID,
    member.shaft.y * METERS_PER_GRID,
  );
  const radiusGrid = definition.rotor?.pitchRadiusGrid ?? definition.wheel?.radiusGrid ?? 0.5;
  const radius = camera.scale * radiusGrid * METERS_PER_GRID;

  ctx.beginPath();
  ctx.moveTo(center.x, center.y);
  // Ekran ma os Y w dol, wiec kat obrotu zmienia znak.
  ctx.lineTo(center.x + Math.cos(-member.angle) * radius, center.y + Math.sin(-member.angle) * radius);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();
}

/** Krotka etykieta przy wale (np. "x2, w druga strone"). */
export function drawShaftLabel(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  shaft: Vec2Like,
  text: string,
  color = '#9fb0c6',
): void {
  const point = camera.worldToScreen(shaft.x * METERS_PER_GRID, shaft.y * METERS_PER_GRID);
  ctx.font = '12px sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.fillText(text, point.x, point.y + camera.scale * 0.42);
}